/**
 * useLiveKitConsultation Hook
 *
 * React hook wrapper around LiveKitClient for easy component integration.
 * Manages LiveKit room connection, audio publishing, and agent communication.
 *
 * Features:
 * - Automatic token fetching from backend
 * - Connection state management
 * - Recording controls (start/stop/pause/resume)
 * - Duration tracking
 * - Event handler registration
 * - Automatic cleanup on unmount
 *
 * Usage:
 * ```tsx
 * const livekit = useLiveKitConsultation({
 *   consultationId: '123',
 *   authToken: 'jwt-token',
 *   onTranscription: handleTranscription,
 *   onCDSS: handleCDSS,
 * });
 *
 * // Connect and start recording
 * await livekit.connect();
 * await livekit.startRecording();
 *
 * // Stop and submit for analysis
 * await livekit.stopRecording();
 * await livekit.submitForAnalysis();
 * ```
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import {
  LiveKitClient,
  type LiveKitEventHandlers,
  type LiveKitClientConfig,
  type LiveKitConnectionState,
  type LiveKitConnectionQuality,
  type SessionInfoResponse,
  type ProcessingStatus,
} from '@/lib/livekit/LiveKitClient';
import { createComponentLogger } from '@/lib/utils/logger';
import { toast } from 'sonner';
import { useAuthStore, type User } from '@/lib/stores/authStore';
import { useAudioStore } from '@/lib/stores/audioStore';
import { initializeForConsultation } from '@/lib/stores/consultationPersistence';

const logger = createComponentLogger('useLiveKitConsultation');

/**
 * Processing status for UI display
 */
export type ProcessingStatusType = ProcessingStatus['status'];

/**
 * Hook options
 */
export interface UseLiveKitConsultationOptions extends LiveKitEventHandlers {
  /** Consultation ID for room creation */
  consultationId: string;

  /** Patient ID for auto-saving DiagnosticReport (optional) */
  patientId?: string;

  /** Authentication token for backend API */
  authToken: string;

  /** LiveKit client configuration (optional) */
  config?: LiveKitClientConfig;

  /** Auto-connect on mount (default: false) */
  autoConnect?: boolean;

  /** Show toast notifications for errors (default: true) */
  showToastNotifications?: boolean;

  /** Whether this is a continuation of a previous session (default: false) */
  isContinuation?: boolean;

  /** JSON-stringified previous CDSS summary for continuation sessions */
  previousSummary?: string;

  /** Pre-fetched vitals data to send to agent (from ConsultationPage's React Query result) */
  vitals?: unknown;

  /** Pre-fetched SOAP note data to send to agent (from ConsultationPage's React Query result) */
  soapData?: unknown;
}

/**
 * Hook return value
 */
export interface UseLiveKitConsultationReturn {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionState: LiveKitConnectionState;
  connectionQuality: LiveKitConnectionQuality;
  isAgentReady: boolean; // Agent has sent session_started acknowledgement

  // Recording state
  isRecording: boolean;
  isPaused: boolean;
  duration: number; // in seconds

  // Analysis state
  isAwaitingSummary: boolean; // Waiting for session summary from agent
  processingStatus: ProcessingStatusType | null; // Current processing stage from backend
  processingErrorMessage: string | null; // Backend-provided detail when processingStatus === 'error'

  // Methods
  connect: () => Promise<void>;
  disconnect: () => void;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  submitForAnalysis: () => Promise<void>;
  resetForRetry: () => void; // Reset submission state for retry
  retrySubmission: () => Promise<void>; // Retry sending session_end (backend has audio)
  quickSubmit: () => Promise<void>; // Send session_quick_end and return immediately — no waiting for a summary

  // LiveKit client instance (for advanced usage)
  client: LiveKitClient | null;

  // Error state
  error: string | null;
}

/**
 * Helper: delay function for retry logic
 */
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Categorize error type for better error messages
 */
function categorizeError(error: unknown, statusCode?: number): string {
  if (statusCode === 401 || statusCode === 403) {
    return 'Authentication failed. Please log in again.';
  }
  if (statusCode && statusCode >= 500) {
    return 'Service temporarily unavailable.';
  }
  if (error instanceof TypeError && String(error).includes('fetch')) {
    return 'Network error. Please check your connection.';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error occurred.';
}

/**
 * Fetch LiveKit token from backend (single attempt)
 */
async function fetchLiveKitToken(
  consultationId: string,
  authToken: string,
  userId?: string,
  userName?: string,
  user?: User | null,
  isContinuation?: boolean,
  previousSummary?: string,
  vitals?: unknown,
  soapData?: unknown,
  patientId?: string
): Promise<{ token: string; room_name: string; server_url: string }> {
  // Use full URL from env (it's already a complete URL like http://46.62.221.77:8088/token)
  const tokenEndpoint =
    import.meta.env.VITE_LIVEKIT_TOKEN_ENDPOINT || 'http://46.62.221.77:8088/agent';

  logger.log('Fetching LiveKit token for consultation:', consultationId);
  logger.log('Token endpoint:', tokenEndpoint);

  // Convert specialization to department_id format (lowercase with underscores)
  // Example: "General Practitioner" -> "general_practitioner"
  const getDepartmentId = (userObj: any): string => {
    // Try to extract from practitionerRole.specialty first
    let specialization = userObj?.practitionerRole?.specialty;

    // Fallback to user.specialization if practitionerRole doesn't have it
    if (!specialization) {
      specialization = userObj?.specialization;
    }

    if (!specialization) {
      logger.log(
        'No specialization found in practitionerRole or user, using default: general_practitioner'
      );
      return 'general_practitioner';
    }

    const departmentId = specialization.toLowerCase().replace(/\s+/g, '_');
    logger.log('Extracted specialization:', specialization, '-> department_id:', departmentId);
    return departmentId;
  };

  // Get department from user practitionerRole or fallback
  const departmentId = getDepartmentId(user);

  // Select the CDSS worker to dispatch. Ayurveda practitioners must be routed to the
  // Ayurveda CDSS agent (which performs Knowledge Base reference-sloka enrichment);
  // everyone else uses the modern-medicine agent. Detection mirrors useIsAyurveda:
  // dispatch is by agent_name (RoomAgentDispatch), so the correct worker only runs
  // when the correct agent_name is sent.
  const isAyurvedaPractitioner = (() => {
    const dept = (user as any)?.practitionerRole?.department?.toLowerCase?.() ?? '';
    const specialty = (user as any)?.practitionerRole?.specialty?.toLowerCase?.() ?? '';
    const specialization = (user as any)?.specialization?.toLowerCase?.() ?? '';
    return (
      dept.includes('ayurveda') ||
      specialty.includes('ayurveda') ||
      specialization.includes('ayurveda')
    );
  })();
  const cdssAgentName = isAyurvedaPractitioner ? 'cdss-ayurveda-agent' : 'cdss-agent';
  logger.log('Selected CDSS agent for dispatch:', cdssAgentName);

  // Get project reference from localStorage (stored during login)
  const projectReference = localStorage.getItem('project-reference') || '';
  // Extract project ID from reference (e.g., "Project/uuid" -> "uuid")
  const projectId = projectReference.includes('/')
    ? projectReference.split('/')[1]
    : projectReference;

  // TODO: temporary ayurveda routing — remove later
  const _dept = user?.practitionerRole?.department?.toLowerCase() ?? '';
  const _spec = user?.practitionerRole?.specialty?.toLowerCase() ?? '';
  const _specz = user?.specialization?.toLowerCase() ?? '';
  const isAyurveda =
    _dept.includes('ayurveda') || _spec.includes('ayurveda') || _specz.includes('ayurveda');

  // Dedicated per-project column (ProjectConfig.medicineCatalogueEnabled), not the
  // generic services table — see pranik-hmis auth.controller.ts.
  const medicineCatalogueEnabled =
    useAuthStore.getState?.().projectConfig?.medicineCatalogueEnabled ?? false;

  const now = new Date();
  const requestBody = {
    appointment_id: `cdss-${consultationId}-${now.getTime()}`,
    role: 'doctor',
    user_id: userId,
    name: userName || 'Doctor',
    agent_name: isAyurveda ? 'cdss-ayurveda-agent' : cdssAgentName,
    // Top-level fields the agent needs to write to Medplum (e.g. for
    // session_quick_end). Also mirrored inside metadata below for agent
    // code paths that read them from there instead.
    medplum_token: authToken,
    project_id: projectId,
    practitioner_id: user?.id || '',
    metadata: {
      encounter_id: consultationId,
      patient_id: patientId || '',
      department_id: departmentId,
      project_id: projectId,
      practitioner_id: user?.id || '',
      medicine_catalogue_enabled: medicineCatalogueEnabled,
      timestamp: now.toISOString(),
      ...(isContinuation && {
        is_continuation: true,
        previous_summary: previousSummary || '{}',
      }),
      ...(vitals &&
      typeof vitals === 'object' &&
      !Array.isArray(vitals) &&
      Object.keys(vitals).length > 0
        ? { vitals }
        : {}),
      ...(soapData &&
      typeof soapData === 'object' &&
      !Array.isArray(soapData) &&
      Object.keys(soapData).length > 0
        ? { soap_notes: soapData }
        : {}),
    },
  };

  logger.log('Request body:', requestBody);

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Note: Backend may not need Authorization header based on curl example
      // but we'll include it if authToken is available
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    logger.error('Token fetch failed:', response.status, errorText);
    throw new Error(`Failed to fetch LiveKit token: ${errorText}`);
  }

  const data = await response.json();
  logger.log('Received LiveKit token:', data);

  // Backend returns: { token, room, url, ... }
  // Map to our expected format
  return {
    token: data.token,
    room_name: data.room || `room-appointment-${consultationId}`,
    server_url: data.url || import.meta.env.VITE_LIVEKIT_SERVER_URL || 'wss://consult.pranik.ai',
  };
}

/**
 * Fetch LiveKit token with retry logic
 *
 * Attempts to fetch token up to 3 times with 1 second delay between attempts.
 * Provides detailed error messages based on failure type.
 */
async function fetchLiveKitTokenWithRetry(
  consultationId: string,
  authToken: string,
  userId?: string,
  userName?: string,
  user?: User | null,
  maxAttempts: number = 3,
  delayMs: number = 1000,
  isContinuation?: boolean,
  previousSummary?: string,
  vitals?: unknown,
  soapData?: unknown,
  patientId?: string
): Promise<{ token: string; room_name: string; server_url: string }> {
  let lastError: Error | null = null;
  let lastStatusCode: number | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      logger.log(`Token fetch attempt ${attempt}/${maxAttempts}...`);

      const result = await fetchLiveKitToken(
        consultationId,
        authToken,
        userId,
        userName,
        user,
        isContinuation,
        previousSummary,
        vitals,
        soapData,
        patientId
      );

      // Validate token exists in response
      if (!result.token) {
        throw new Error('Token not received in response');
      }

      logger.log(`Token fetch successful on attempt ${attempt}`);
      return result;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Try to extract status code from error message
      const statusMatch = lastError.message.match(/status[:\s]+(\d{3})/i);
      if (statusMatch) {
        lastStatusCode = parseInt(statusMatch[1], 10);
      }

      logger.warn(`Token fetch attempt ${attempt} failed:`, lastError.message);

      // Don't delay after the last attempt
      if (attempt < maxAttempts) {
        logger.log(`Waiting ${delayMs}ms before retry...`);
        await delay(delayMs);
      }
    }
  }

  // All attempts failed - create user-friendly error message
  const errorMessage = categorizeError(lastError, lastStatusCode);
  logger.error(`All ${maxAttempts} token fetch attempts failed. Last error:`, lastError?.message);

  throw new Error(
    `Unable to connect to consultation service after ${maxAttempts} attempts. ${errorMessage} Please try again later.`
  );
}

/**
 * useLiveKitConsultation - Main hook
 */
export function useLiveKitConsultation(
  options: UseLiveKitConsultationOptions
): UseLiveKitConsultationReturn {
  const {
    consultationId,
    patientId,
    authToken,
    config,
    autoConnect = false,
    showToastNotifications = true,
    isContinuation = false,
    previousSummary,
    vitals,
    soapData,
    onTranscription,
    onCDSS,
    onSessionSummary,
    onError,
    onReady,
    onConnectionStateChange,
    onConnectionQualityChange,
  } = options;

  // Get user from auth store
  const user = useAuthStore((state) => state.user);

  // Get audio store for persisting recorded audio
  const setAudioRecording = useAudioStore((state) => state.setAudioRecording);

  // Client ref
  const clientRef = useRef<LiveKitClient | null>(null);

  // State
  const [connectionState, setConnectionState] = useState<LiveKitConnectionState>('disconnected');
  const [connectionQuality, setConnectionQuality] = useState<LiveKitConnectionQuality>('unknown');
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isAwaitingSummary, setIsAwaitingSummary] = useState(false);
  const [isAgentReady, setIsAgentReady] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatusType | null>(null);
  const [processingErrorMessage, setProcessingErrorMessage] = useState<string | null>(null);

  // Duration timer ref
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);

  // Last recorded audio blob for persistence
  const lastRecordedBlobRef = useRef<Blob | null>(null);

  // Refs for props that are read inside connect() — avoids stale closures without
  // recreating connect (which would retrigger the autoConnect effect mid-session)
  const vitalsRef = useRef(vitals);
  const soapDataRef = useRef(soapData);
  const isContinuationRef = useRef(isContinuation);
  const previousSummaryRef = useRef(previousSummary);
  const patientIdRef = useRef(patientId);
  useEffect(() => {
    vitalsRef.current = vitals;
  }, [vitals]);
  useEffect(() => {
    soapDataRef.current = soapData;
  }, [soapData]);
  useEffect(() => {
    isContinuationRef.current = isContinuation;
  }, [isContinuation]);
  useEffect(() => {
    previousSummaryRef.current = previousSummary;
  }, [previousSummary]);
  useEffect(() => {
    patientIdRef.current = patientId;
  }, [patientId]);

  // Submission tracking refs - prevent duplicate submissions
  // hasSubmittedRef: true once session_end is sent (may still need to receive summary)
  // isSubmittingRef: true while waiting for summary (allows reconnection)
  // hasSummaryReceivedRef: true once summary is received (blocks reconnection)
  const hasSubmittedRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const hasSummaryReceivedRef = useRef(false);

  /**
   * Internal error handler
   * Ignores errors that occur during/after submission (expected during shutdown)
   * But NOT if we're still waiting for summary - those errors matter!
   */
  const handleError = useCallback(
    (errorData: { message: string; code?: string }) => {
      // Only ignore errors after summary is received
      if (hasSummaryReceivedRef.current) {
        logger.log('Ignoring error after summary received:', errorData);
        return;
      }

      // During submission flow, log but still track the error
      if (isSubmittingRef.current) {
        logger.log('Error during submission flow:', errorData);
        // Don't show toast during submission - we're handling it
        onError?.(errorData);
        return;
      }

      logger.error('LiveKit error:', errorData);
      setError(errorData.message);

      if (showToastNotifications) {
        toast.error(`LiveKit Error: ${errorData.message}`);
      }

      // Call user's error handler
      onError?.(errorData);
    },
    [onError, showToastNotifications]
  );

  /**
   * Internal connection state change handler
   * Suppresses toast notifications during submission flow (not helpful to user)
   * but still tracks state changes
   */
  const handleConnectionStateChange = useCallback(
    (state: LiveKitConnectionState) => {
      logger.log('Connection state changed:', state);
      setConnectionState(state);
      onConnectionStateChange?.(state);

      // Suppress toasts during submission flow - reconnection is expected and handled
      // User doesn't need to see "Reconnecting..." while waiting for summary
      if (isSubmittingRef.current && !hasSummaryReceivedRef.current) {
        logger.log('Suppressing connection toast during submission flow');
        return;
      }

      // After summary received, also suppress - we're done
      if (hasSummaryReceivedRef.current) {
        logger.log('Suppressing connection toast after summary received');
        return;
      }

      // Show notification for connection state during normal operation
      if (showToastNotifications) {
        switch (state) {
          case 'connected':
            toast.success('Connected to LiveKit');
            break;
          case 'reconnecting':
            toast.info('Reconnecting...');
            break;
          case 'disconnected':
            if (clientRef.current?.isConnected()) {
              toast.error('Disconnected from LiveKit');
            }
            break;
        }
      }
    },
    [onConnectionStateChange, showToastNotifications]
  );

  /**
   * Internal connection quality change handler
   */
  const handleConnectionQualityChange = useCallback(
    (quality: LiveKitConnectionQuality) => {
      logger.log('Connection quality changed:', quality);
      setConnectionQuality(quality);
      onConnectionQualityChange?.(quality);

      // Show warning for poor connection
      if (quality === 'poor' && showToastNotifications) {
        toast.warning('Poor connection quality');
      }
    },
    [onConnectionQualityChange, showToastNotifications]
  );

  /**
   * Connect to LiveKit room
   */
  const connect = useCallback(async () => {
    if (clientRef.current?.isConnected()) {
      logger.warn('Already connected to LiveKit room');
      return;
    }

    setError(null);
    setConnectionState('connecting');

    // Initialize persistence for this consultation (clears old data if different consultation)
    await initializeForConsultation(consultationId);

    try {
      // Get user information for token request
      const userId = user?.id || 'default-user-id';
      const userName =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.username || 'Doctor';

      logger.log('User info:', { userId, userName });
      logger.log('User object for department_id extraction:', user);

      // Fetch token from backend with retry logic (3 attempts, 1 second delay)
      logger.log('Fetching LiveKit token from backend...');
      const { token, room_name, server_url } = await fetchLiveKitTokenWithRetry(
        consultationId,
        authToken,
        userId,
        userName,
        user,
        3,
        1000,
        isContinuationRef.current,
        previousSummaryRef.current,
        vitalsRef.current ?? null,
        soapDataRef.current ?? null,
        patientIdRef.current
      );

      // Create LiveKit client
      const client = new LiveKitClient(
        {
          onTranscription,
          onCDSS: (data) => {
            logger.log('CDSS data received:', data);
            onCDSS?.(data);
          },
          onSessionSummary: async (data: SessionInfoResponse) => {
            logger.log('Session summary received:', data);

            // Guard: If summary was already processed, skip duplicate processing
            if (hasSummaryReceivedRef.current) {
              logger.warn('Summary already processed, ignoring duplicate callback');
              return;
            }

            // Mark summary as received - this stops reconnection attempts and suppresses toasts
            hasSummaryReceivedRef.current = true;
            setIsAwaitingSummary(false);

            onSessionSummary?.(data);
          },
          onSessionEndAck: (ack) => {
            logger.log('Session end acknowledged by backend:', {
              message_id: ack.message_id,
              audio_chunks_received: ack.audio_chunks_received,
              processing_started: ack.processing_started,
            });
            // This confirms the backend received the session_end request
            // No state change needed, just log for debugging
          },
          onProcessingStatus: (status) => {
            logger.log('Processing status update:', {
              status: status.status,
              progress: status.progress_percent,
            });
            // Update processing status for UI display
            setProcessingStatus(status.status);

            if (status.status === 'error') {
              logger.error('Processing status error:', status.error_message);
              setProcessingErrorMessage(status.error_message ?? null);
              if (showToastNotifications) {
                toast.error(status.error_message || 'Processing failed');
              }
            } else {
              setProcessingErrorMessage(null);
            }
          },
          onError: handleError,
          onReady: () => {
            logger.log('Agent ready - session_started received');
            setIsAgentReady(true);
            onReady?.();
          },
          onConnectionStateChange: handleConnectionStateChange,
          onConnectionQualityChange: handleConnectionQualityChange,
        },
        config
      );

      // Connect to room
      await client.connect(token, server_url);

      clientRef.current = client;
      logger.log('Connected to LiveKit room:', room_name);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect';

      // Check if this is a connection refused error (backend not available)
      const isBackendUnavailable =
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('NetworkError') ||
        errorMessage.includes('ERR_CONNECTION_REFUSED');

      if (isBackendUnavailable) {
        logger.warn(
          'LiveKit backend endpoint not available. Backend may not have implemented /api/livekit/token yet.'
        );
        setError('LiveKit backend not available');
      } else {
        logger.error('Failed to connect to LiveKit:', err);
        setError(errorMessage);
      }

      setConnectionState('disconnected');

      if (showToastNotifications) {
        if (isBackendUnavailable) {
          toast.warning('LiveKit not available - Backend endpoint missing');
        } else {
          toast.error(`Connection failed: ${errorMessage}`);
        }
      }

      throw err;
    }
  }, [
    consultationId,
    authToken,
    user,
    config,
    onTranscription,
    onCDSS,
    onReady,
    handleError,
    handleConnectionStateChange,
    handleConnectionQualityChange,
    showToastNotifications,
  ]);

  /**
   * Disconnect from LiveKit room
   */
  const disconnect = useCallback(() => {
    logger.log('Disconnecting from LiveKit room...');

    // Stop recording if active
    if (isRecording) {
      stopRecording();
    }

    // Disconnect client
    if (clientRef.current) {
      clientRef.current.disconnect();
      clientRef.current = null;
    }

    setConnectionState('disconnected');
    setConnectionQuality('unknown');
    logger.log('Disconnected from LiveKit room');
  }, [isRecording]);

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async () => {
    if (!clientRef.current) {
      throw new Error('Not connected to LiveKit room. Call connect() first.');
    }

    if (isRecording && !isPaused) {
      logger.warn('Already recording');
      return;
    }

    try {
      logger.log('Starting audio recording...');

      // If paused, just resume
      if (isPaused) {
        resumeRecording();
        return;
      }

      // Start publishing audio
      await clientRef.current.startAudioPublishing();

      setIsRecording(true);
      setIsPaused(false);

      // Start duration timer
      recordingStartTimeRef.current = Date.now();
      pausedTimeRef.current = 0;

      durationTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - recordingStartTimeRef.current - pausedTimeRef.current;
        const currentDuration = Math.floor(elapsed / 1000);
        setDuration(currentDuration);
      }, 1000);

      logger.log('Audio recording started');

      if (showToastNotifications) {
        toast.success('Recording started');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
      logger.error('Failed to start recording:', err);
      setError(errorMessage);

      if (showToastNotifications) {
        toast.error(`Failed to start recording: ${errorMessage}`);
      }

      throw err;
    }
  }, [isRecording, isPaused, showToastNotifications]);

  /**
   * Stop recording audio
   */
  const stopRecording = useCallback(async () => {
    if (!clientRef.current || !isRecording) {
      logger.warn('Not recording');
      return;
    }

    try {
      logger.log('Stopping audio recording...');

      // Stop publishing audio and get recorded blob
      const recordedBlob = await clientRef.current.stopAudioPublishing();

      // Save recorded audio for playback
      if (recordedBlob && recordedBlob.size > 0) {
        logger.log('Saving recorded audio', { size: recordedBlob.size, duration });
        lastRecordedBlobRef.current = recordedBlob;

        // Save to audio store (which also persists to IndexedDB)
        setAudioRecording(consultationId, recordedBlob, duration);
      } else {
        logger.warn('No recorded audio blob available');
      }

      setIsRecording(false);
      setIsPaused(false);

      // Clear duration timer
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }

      logger.log('Audio recording stopped. Duration:', duration, 'seconds');

      if (showToastNotifications) {
        toast.success('Recording stopped');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to stop recording';
      logger.error('Failed to stop recording:', err);
      setError(errorMessage);

      if (showToastNotifications) {
        toast.error(`Failed to stop recording: ${errorMessage}`);
      }
    }
  }, [isRecording, duration, showToastNotifications, consultationId, setAudioRecording]);

  /**
   * Pause recording (mute audio to stop sending data, but keep connection)
   * This actually mutes the microphone so no audio is sent to backend during pause
   */
  const pauseRecording = useCallback(async () => {
    if (!isRecording || isPaused) {
      logger.warn('Cannot pause: not recording or already paused');
      return;
    }

    logger.log('Pausing audio recording...');

    // IMPORTANT: Mute the audio track to stop sending audio to backend
    // This prevents the audio from accumulating on the server while paused
    try {
      await clientRef.current?.muteAudio();
      logger.log('Audio track muted - no audio will be sent to backend');
    } catch (error) {
      logger.error('Failed to mute audio track:', error);
      // Continue with pause anyway - UI should still show paused state
    }

    setIsPaused(true);

    // Record when pause started
    const pauseStartTime = Date.now();

    // Clear duration timer
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }

    // Calculate total paused time when resumed
    pausedTimeRef.current += pauseStartTime - recordingStartTimeRef.current;

    logger.log('Audio recording paused (audio muted)');

    if (showToastNotifications) {
      toast.info('Recording paused');
    }
  }, [isRecording, isPaused, showToastNotifications]);

  /**
   * Resume recording after pause (unmute audio to resume sending data)
   */
  const resumeRecording = useCallback(async () => {
    if (!isRecording || !isPaused) {
      logger.warn('Cannot resume: not recording or not paused');
      return;
    }

    logger.log('Resuming audio recording...');

    // IMPORTANT: Unmute the audio track to resume sending audio to backend
    try {
      await clientRef.current?.unmuteAudio();
      logger.log('Audio track unmuted - audio is now being sent to backend');
    } catch (error) {
      logger.error('Failed to unmute audio track:', error);
      // Continue with resume anyway
    }

    setIsPaused(false);

    // Restart duration timer
    const resumeTime = Date.now();
    durationTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - resumeTime + duration * 1000;
      setDuration(Math.floor(elapsed / 1000));
    }, 1000);

    logger.log('Audio recording resumed (audio unmuted)');

    if (showToastNotifications) {
      toast.success('Recording resumed');
    }
  }, [isRecording, isPaused, duration, showToastNotifications]);

  /**
   * Submit for final analysis
   * Sends session_end signal on session_info topic and waits for summary
   * This is robust and handles edge cases gracefully
   */
  const submitForAnalysis = useCallback(async () => {
    // Prevent duplicate submissions
    if (hasSubmittedRef.current) {
      logger.warn('Already submitted, ignoring duplicate submission');
      return;
    }

    if (isSubmittingRef.current) {
      logger.warn('Already submitting, ignoring duplicate submission');
      return;
    }

    // Mark as submitting immediately
    isSubmittingRef.current = true;
    hasSubmittedRef.current = true;
    hasSummaryReceivedRef.current = false; // Reset in case of retry

    try {
      logger.log('Submitting for analysis - sending session_end signal...');
      setIsAwaitingSummary(true);
      setProcessingStatus(null); // Reset processing status for new submission
      setProcessingErrorMessage(null);

      // Try to send session_end signal (will handle closed connection gracefully)
      if (clientRef.current) {
        await clientRef.current.sendSessionEnd();
        logger.log('Session end signal sent, waiting for summary...');
      } else {
        logger.warn('No LiveKit client available, skipping session end signal');
      }

      if (showToastNotifications) {
        toast.info('Processing consultation... Please wait');
      }
    } catch (err) {
      // Only show error for unexpected failures
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit for analysis';

      // Ignore "PC manager is closed" errors - these are expected during shutdown
      if (errorMessage.includes('PC manager is closed') || errorMessage.includes('not connected')) {
        logger.log('Connection already closed during submit - this is expected');
      } else {
        logger.error('Failed to submit for analysis:', err);
        setError(errorMessage);
        setIsAwaitingSummary(false);

        if (showToastNotifications) {
          toast.error(`Failed to submit: ${errorMessage}`);
        }

        // Reset flags on actual error so user can retry
        isSubmittingRef.current = false;
        hasSubmittedRef.current = false;
        throw err;
      }
    } finally {
      // Keep hasSubmittedRef as true but mark submitting as false
      isSubmittingRef.current = false;
    }
  }, [consultationId, duration, showToastNotifications]);

  /**
   * Quick Submit — sends session_quick_end and returns immediately.
   *
   * Unlike submitForAnalysis, this does not wait for a summary: the agent
   * sends nothing back on this path and instead writes the summary directly
   * to Medplum as a DiagnosticReport in the background. Callers should
   * navigate away right after this resolves.
   */
  const quickSubmit = useCallback(async () => {
    if (hasSubmittedRef.current) {
      logger.warn('Already submitted, ignoring duplicate quick submit');
      return;
    }

    hasSubmittedRef.current = true;

    try {
      logger.log('Quick submitting - sending session_quick_end signal...');
      if (clientRef.current) {
        await clientRef.current.sendSessionQuickEnd();
        logger.log('Session quick-end signal sent');
      } else {
        logger.warn('No LiveKit client available, skipping quick-end signal');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to quick submit';
      if (errorMessage.includes('PC manager is closed') || errorMessage.includes('not connected')) {
        logger.log('Connection already closed during quick submit - this is expected');
      } else {
        // Don't surface an error toast or reset submission state here — the
        // doctor is navigating to the dashboard regardless, and the local
        // ConsultationRecord save already happened independently.
        logger.error('Failed to quick submit:', err);
      }
    }
  }, []);

  /**
   * Reset submission state for retry
   * Call this when submission times out or fails and user wants to retry
   * Note: For retry, we just send session_end again - backend has the audio
   */
  const resetForRetry = useCallback(() => {
    logger.log('Resetting submission state for retry');
    isSubmittingRef.current = false;
    hasSubmittedRef.current = false;
    hasSummaryReceivedRef.current = false;
    setIsAwaitingSummary(false);
    setProcessingStatus(null); // Reset processing status
    setProcessingErrorMessage(null);
    setError(null);

    // Also reset client state if available
    if (clientRef.current) {
      clientRef.current.resetSubmissionState();
    }
  }, []);

  /**
   * Retry sending session_end
   * Backend already has the audio, so we just need to resend the end signal
   */
  const retrySubmission = useCallback(async () => {
    logger.log('Retrying submission - resending session_end');

    if (hasSummaryReceivedRef.current) {
      logger.warn('Summary already received, no need to retry');
      return;
    }

    // Reset state for retry
    resetForRetry();

    // Now submit again
    await submitForAnalysis();
  }, [resetForRetry, submitForAnalysis]);

  /**
   * Auto-connect on mount if enabled
   */
  useEffect(() => {
    if (autoConnect) {
      connect().catch((err) => {
        logger.error('Auto-connect failed:', err);
      });
    }
  }, [autoConnect, connect]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      logger.log('Cleaning up useLiveKitConsultation...');

      // Clear duration timer
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }

      // Disconnect client
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
    };
  }, []);

  return {
    // Connection state
    isConnected: connectionState === 'connected',
    isConnecting: connectionState === 'connecting',
    connectionState,
    connectionQuality,
    isAgentReady,

    // Recording state
    isRecording,
    isPaused,
    duration,

    // Analysis state
    isAwaitingSummary,
    processingStatus,
    processingErrorMessage,

    // Methods
    connect,
    disconnect,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    submitForAnalysis,
    resetForRetry,
    retrySubmission,
    quickSubmit,

    // Client instance
    client: clientRef.current,

    // Error state
    error,
  };
}
